// src/public/tests/WordAssociationExamples.js
import { useState } from 'react';
import {
    Container, Paper, Typography, Button, Box, Radio, RadioGroup,
    FormControlLabel, Alert, Divider, LinearProgress, Stack
} from '@mui/material';
import { CheckCircle, Cancel, PlayArrow } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const example = {
    id: 'E1',
    pair1: ['Barco', 'navegar'],
    options: [
        ['Bicicleta', 'carro'],
        ['Canoa', 'remo'],
        ['Alfombra', 'mesa'],
        ['Pierna', 'zapato']
    ],
    correctAnswer: 1
};

const WordAssociationExamples = ({ onStartTest, loading }) => {
    const { t } = useTranslation();
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [feedback, setFeedback] = useState(null);

    const handleAnswerChange = (value) => {
        const selectedValue = parseInt(value, 10);
        setSelectedAnswer(selectedValue);

        const isCorrect = selectedValue === example.correctAnswer;
        
        setFeedback({
            isCorrect,
            correctAnswer: example.correctAnswer
        });
    };

    const optionLabels = ['A', 'B', 'C', 'D'];

    return (
        <Container maxWidth="md">
            <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
                <Typography variant="h4" gutterBottom align="center">
                    {t('reasoningTests.practiceExamples')}
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
                    {t('reasoningTests.practiceExamplesDescription')}
                </Typography>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('reasoningTests.example')} 1 {t('reasoningTests.of')} 1
                    </Typography>
                    <LinearProgress variant="determinate" value={100} sx={{ height: 8, borderRadius: 1 }} />
                </Box>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('reasoningTests.similarRelationTo')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: 'primary.main' }}>
                        {example.pair1[0]} : {example.pair1[1]}
                    </Typography>

                    <RadioGroup
                        value={selectedAnswer !== null ? String(selectedAnswer) : ''}
                        onChange={(e) => handleAnswerChange(e.target.value)}
                    >
                        <Stack spacing={2}>
                            {example.options.map((option, index) => (
                                <FormControlLabel
                                    key={index}
                                    value={String(index)}
                                    disabled={feedback?.isCorrect}
                                    control={<Radio />}
                                    label={
                                        <Typography variant="body1">
                                            <strong>{optionLabels[index]}.</strong> {option[0]} : {option[1]}
                                        </Typography>
                                    }
                                    sx={{
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 2,
                                        m: 0,
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                        },
                                    }}
                                />
                            ))}
                        </Stack>
                    </RadioGroup>
                </Box>

                {feedback && (
                    <Alert 
                        severity={feedback.isCorrect ? 'success' : 'error'}
                        icon={feedback.isCorrect ? <CheckCircle /> : <Cancel />}
                        sx={{ mt: 3 }}
                    >
                        {feedback.isCorrect ? (
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {t('reasoningTests.correct')}
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {t('reasoningTests.incorrect')}
                                </Typography>
                                <Typography variant="body2">
                                    {t('reasoningTests.correctAnswerIs')}: <strong>{optionLabels[feedback.correctAnswer]}.</strong> {example.options[feedback.correctAnswer][0]} : {example.options[feedback.correctAnswer][1]}
                                </Typography>
                            </>
                        )}
                    </Alert>
                )}

                <Divider sx={{ my: 4 }} />

                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {t('reasoningTests.readyToStart')}
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<PlayArrow />}
                        onClick={onStartTest}
                        disabled={loading}
                        sx={{ minWidth: '200px' }}
                    >
                        {t('reasoningTests.startTest')}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default WordAssociationExamples;